import { Grid } from '@mui/material';
import { OptimizerConfig } from '@nx-smartmonkey/shared/domain';
import { downloadJsonFile, toDayNoon } from '@nx-smartmonkey/shared/helpers';
import { RoutalDialog, RoutalSelect, RoutalSelectMultiSelectOption, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import countries from '../constants/Countries';
import { parseCountries } from '../constants/OptimizerConfig';
import { Plan } from '../domain/plan/Plan';
import { PlanFlat } from '../domain/plan/PlanFlat';
import { PlanStatus } from '../domain/plan/PlanStatus';
import { saveData } from '../helpers/excel/Common';
import { exportPlan } from '../helpers/excel/Plans';
import { useRetrieveOrganization } from '../hooks/organization/useRetrieveOrganization';
import { useRetrieveActivePlan } from '../hooks/plan/useRetrieveActivePlan';
import { useRetrieveActivePlanDrivers } from '../hooks/plan/useRetrieveActivePlanDrivers';
import { useRetrieveActivePlanGeoFences } from '../hooks/plan/useRetrieveActivePlanGeoFences';
import { useRetrieveActivePlanStops } from '../hooks/plan/useRetrieveActivePlanStops';
import { useUpdateActivePlan } from '../hooks/plan/useUpdateActivePlan';
import { useRetrieveActiveProject } from '../hooks/project/useRetrieveActiveProject';
import { useAsUser } from '../hooks/session/useAsUser';
import { useRetrieveUser } from '../hooks/user/useRetrieveUser';
import { PlanFlatMapper } from '../mappers/plan/PlanFlatMapper';
import { PlanMapper } from '../mappers/plan/PlanMapper';
import { ProjectCustomFieldsMapper } from '../mappers/project/ProjectCustomFieldsMapper';
import CountryAutocomplete from '../react-components/Autocomplete';
import Button from '../react-components/Button';
import InputText from '../react-components/InputText';
import Text from '../react-components/Text';
import { InputDatePicker } from '../react-components/input-date-picker/InputDatePicker';

type ActivePlanSettingsDialogProps = {
  dialogOpen: boolean;
  onUpdatePlanSettings: () => void;
  onCloseDialog: () => any;
};

export const ActivePlanSettingsDialog = ({
  dialogOpen,
  onUpdatePlanSettings,
  onCloseDialog,
}: ActivePlanSettingsDialogProps) => {
  const { data: user } = useRetrieveUser();
  const { data: activePlan } = useRetrieveActivePlan();
  const { data: drivers } = useRetrieveActivePlanDrivers();
  const { data: stops } = useRetrieveActivePlanStops();
  const { data: geoFences } = useRetrieveActivePlanGeoFences();
  const { data: loggedUser } = useRetrieveUser();
  const { asUser } = useAsUser();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: organization } = useRetrieveOrganization();
  const [planLabel, setPlanLabel] = useState<string>(``);
  const [planExecutionDate, setPlanExecutionDate] = useState<Date>(new Date());
  const [planStatus, setPlanStatus] = useState<PlanStatus | undefined>(undefined);
  const [planStopDuration, setPlanStopDuration] = useState<number | undefined>(undefined);
  const [planOperationCountry, setPlanOperationCountry] = useState<string | null>(null);

  const { success: updateSuccess, isLoading: isUpdatingSettings, run: updatePlansSettings } = useUpdateActivePlan();

  useEffect(() => {
    if (activePlan) {
      setPlanLabel(activePlan.label ?? ``);
      setPlanStatus(activePlan.status);
      setPlanExecutionDate(new Date(activePlan.execution_date));
      setPlanStopDuration(activePlan.optimizer_config?.service_duration);
      setPlanOperationCountry(activePlan.optimizer_config?.operation_country ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      onUpdatePlanSettings();
      onCloseDialog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateSuccess]);

  const preparePlanData = (planFlat: PlanFlat): Plan => {
    const planToExport = PlanMapper.toDomain(
      {
        ...PlanFlatMapper.toDTO(planFlat),
        stops: [],
        drivers: [],
        geo_fences: [],
      },
      activeProject!.custom_fields!
    );

    planToExport.stops = stops ?? [];
    planToExport.drivers = drivers ?? [];
    planToExport.geo_fences = geoFences ?? [];

    return planToExport;
  };

  const exportExcel = () => {
    if (activeProject && activePlan) {
      let allCustomFields = [];

      if (activeProject.custom_fields) {
        const projectCustomFields = ProjectCustomFieldsMapper.toDTO(activeProject.custom_fields);
        if (organization?.subscription.plan === `monkey`) {
          projectCustomFields.service_report_completed = [];
          projectCustomFields.service_report_task_completed = [];
          projectCustomFields.service_report_canceled = [];
          projectCustomFields.service_report_task_canceled = [];
        }

        allCustomFields = Object.values(projectCustomFields).reduce((prev, curr) => [...prev, ...curr], []);
      }

      const planToExport = preparePlanData(activePlan);
      saveData(
        exportPlan({
          plan: planToExport,
          activeProject: activeProject!,
          customFields: allCustomFields,
        }),
        `ExportPlan_${activePlan.label ?? ``}`
      );
    }
  };

  const exportJson = () => {
    if (activePlan) {
      const planToExport = preparePlanData(activePlan);
      downloadJsonFile(PlanMapper.toDTO(planToExport), `ExportPlan_${activePlan.label ?? ``}`);
    }
  };

  return (
    <RoutalDialog
      open={dialogOpen}
      onCloseDialog={onCloseDialog}
      id="active-plan-settings-dialog"
      titleContent={<Text variant="h6" intlId="plan_settings_dialog.title" />}
      titleRightContent={
        <Button
          id="change_status"
          disabled={!planStatus || isUpdatingSettings}
          onClick={() => {
            if (planStatus) {
              updatePlansSettings({
                label: planLabel,
                status: planStatus,
                execution_date: planExecutionDate,
                optimizer_config: OptimizerConfig.create({
                  ...activePlan?.optimizer_config?.getValue(),
                  service_duration: planStopDuration,
                  operation_country: planOperationCountry ?? undefined,
                }),
              });
            }
          }}
          intlId="plan_settings_dialog.button"
        />
      }
      showFooter={false}
    >
      <Grid container direction="column">
        <Grid item>
          <Text intlId="plan_settings_dialog.plan_label" variant="label" style={{ marginBottom: `8px` }} />
          <InputText
            inputContainerStyles={{
              width: `100%`,
              backgroundColor: `white`,
            }}
            inputStyles={{
              color: `#393939`,
            }}
            type="text"
            fieldValue={planLabel}
            handleEnter={(val: any) => {
              setPlanLabel(val);
            }}
            placeholderIntlId="plan_settings_dialog.plan_label"
          />
        </Grid>

        <VSpacer medium />

        <Grid item>
          <InputDatePicker
            labelIntlId="plan_settings_dialog.plan_execution_date"
            dateValue={planExecutionDate}
            onChange={(date: Date) => {
              setPlanExecutionDate(toDayNoon(date));
            }}
            locale={user?.language.value}
            portalId="active-plan-settings-dialog"
          />
        </Grid>

        <VSpacer medium />

        <Grid item>
          <Text intlId="plan_settings_dialog.plan_status" variant="label" style={{ marginBottom: `8px` }} />

          <RoutalSelect
            id="select-status"
            popoverProps={{
              options: [`in_progress`, `planning`, `finished`, `draft`].map(
                (status): RoutalSelectMultiSelectOption => ({
                  key: status,
                  listItemStyles: {
                    color: `#929599`,
                    fontSize: `12px`,
                    paddingLeft: `9px`,
                    paddingRight: `8px`,
                    overflow: `hidden`,
                  },
                  onClick: () => {
                    setPlanStatus(status as PlanStatus);
                  },
                  content: (
                    <Grid container direction="row" wrap="nowrap" alignItems="center" style={{ overflow: `hidden` }}>
                      <Text
                        tagName="span"
                        style={{
                          textAlign: `justify`,
                          whiteSpace: `nowrap`,
                          overflow: `hidden`,
                          width: `100%`,
                          textOverflow: `ellipsis`,
                          cursor: `pointer`,
                        }}
                        intlId={`planner.plans.${status}`}
                      />
                    </Grid>
                  ),
                })
              ),
            }}
          >
            <Text
              style={{
                cursor: `pointer`,
                width: `100%`,
                textAlign: `justify`,
              }}
              intlId={planStatus ? `plan_settings_dialog.status.${planStatus}` : `plan_settings_dialog.select_status`}
            />
          </RoutalSelect>
        </Grid>

        <VSpacer medium />

        <Grid item>
          <Text intlId="plan_settings_dialog.stop_duration" variant="label" style={{ marginBottom: `8px` }} />

          <InputText
            inputContainerStyles={{
              width: `100%`,
              backgroundColor: `white`,
            }}
            inputStyles={{
              color: `#393939`,
            }}
            type="number"
            fieldValue={planStopDuration}
            handleEnter={(val: any) => {
              console.info(`plan stop duration update:`, val);
              setPlanStopDuration(val);
            }}
            placeholderIntlId="min"
            suffix="min"
          />
        </Grid>

        <VSpacer medium />

        <Grid item>
          <Text intlId="plan_settings_dialog.operation_country" variant="label" style={{ marginBottom: `8px` }} />

          <CountryAutocomplete
            id="operation_country"
            fieldValue={planOperationCountry ?? undefined}
            options={parseCountries(countries, loggedUser?.language)}
            fullWidth
            inputProps={{
              variant: `outlined`,
            }}
            onUpdate={(newValue: string | null) => {
              setPlanOperationCountry(newValue);
            }}
          />
        </Grid>

        <VSpacer medium />

        <Grid item container direction="column" alignItems="flex-start">
          <Grid item>
            <Text variant="body-large" isBold intlId="plan_settings_dialog.export_data.title" />
          </Grid>

          <VSpacer small />

          <Button
            id="download_excel"
            variant="flat"
            styleType="primary"
            onClick={exportExcel}
            intlId="plan_settings_dialog.export_data.download_excel"
          />

          {loggedUser?.isStaff(asUser) ? (
            <Button
              id="download_json"
              variant="flat"
              styleType="primary"
              onClick={exportJson}
              intlId="plan_settings_dialog.export_data.download_json"
            />
          ) : null}
        </Grid>
      </Grid>
    </RoutalDialog>
  );
};
